// Gevel & crepi: de progressieve ladder uit prijslijst A of B toegepast op een calculatie.
// De gewone matcher blijft de opstartfiche koppelen aan de prijslijst van de ploeg; deze
// module neemt daarna de crepi-, gevelisolatie- en steenstripsregels over en prijst ze
// volgens de gekozen variant (SPEC_wijzigingen 1). Pure functies.
import { z } from 'zod';
import { berekenCrepi, crepiLadderSchema, round2, type CrepiResultaat } from './schijven.js';
import type { MatchedLine, MeerwerkLine, ParsedItem, SkippedItem } from './types.js';

export const gevelVariantSchema = z.enum(['A', 'B']);
export type GevelVariant = z.infer<typeof gevelVariantSchema>;

export const GEVEL_VARIANT_LABEL: Record<GevelVariant, string> = {
  A: 'A, onderaannemer met eigen materiaal',
  B: 'B, onderaannemer met ons materiaal',
};

/** Prijslijstcode per variant — de stabiele sleutel over versies heen. */
export const GEVEL_PRICELIST_CODE: Record<GevelVariant, string> = { A: 'gevel_A', B: 'gevel_B' };

/**
 * Snapshot van de gevelprijslijst op de calculatie. Een latere prijswijziging verandert
 * een bestaande calculatie nooit stil: enkel "verversen" laadt de actuele versie opnieuw.
 */
export const gevelConfigSchema = z.object({
  variant: gevelVariantSchema,
  /** De standaard van de onderaannemer op het moment van kiezen (voor de afwijkingscontrole). */
  standaardVariant: gevelVariantSchema,
  pricelistId: z.string(),
  pricelistCode: z.string(),
  versie: z.number().int(),
  geldigVanaf: z.string().nullable(),
  ladder: crepiLadderSchema,
  steenstrips: z.object({ zonderVoeg: z.number(), metVoeg: z.number() }),
  /** Keuze bevestigd via de pop-up. Zolang false kan de calculatie niet op goedgekeurd. */
  bevestigd: z.boolean(),
  bevestigdDoor: z.string().nullable().default(null),
  bevestigdOp: z.string().nullable().default(null),
  /** Accessoires en extra werken uit dezelfde prijslijstversie (SPEC_calculatie 7.1), per postcode.
   *  Ontbreekt bij calculaties van vóór 27 oktober 2026: die houden de prijs van de algemene lijst. */
  accessoires: z.record(z.string(), z.object({ naam: z.string(), eenheid: z.string(), prijs: z.number() })).optional(),
});
export type GevelConfig = z.infer<typeof gevelConfigSchema>;

/** Wat de client bij het opslaan over de gevelprijslijst meestuurt: enkel de keuze. De
 *  tarieven haalt de server zelf uit de opgegeven prijslijstversie. */
export const gevelKeuzeSchema = z.object({
  variant: gevelVariantSchema,
  pricelistId: z.string(),
  bevestigd: z.boolean(),
});
export type GevelKeuze = z.infer<typeof gevelKeuzeSchema>;

export type GevelRol = 'crepi' | 'gevel_isolatie' | 'steenstrips';

/**
 * Herkenning van de gevelposten in de bestaande prijslijsten (naam arbeid), volgens
 * SPEC_calculatie 6.1 en 7.2:
 *  - siliconenharspleister (PLAATSEN VAN CREPI) telt mee in m2 crepi totaal;
 *  - gevelisolatie, EPS én PIR (beslissing Jordy 1 okt 2026), ongeacht de dikte, telt mee in
 *    m2 met isolatie en wordt niet meer apart betaald;
 *  - steenstrips 2 cm krijgen de vaste prijs van de variant (met of zonder voeg).
 * Siliconenharspleister met baksteenmotief blijft een gewone post.
 */
export const GEVEL_ROLLEN: Record<string, GevelRol> = {
  'PLAATSEN VAN CREPI': 'crepi',
  'PLAATSEN VAN EPS ISOLATIE': 'gevel_isolatie',
  'PLAATSEN VAN PIR ISOLATIE GEVEL': 'gevel_isolatie',
  'STEENSTRIPS INCLUSIEF ISOLATIE': 'steenstrips',
};

export function gevelRol(naamArbeid: string | null | undefined): GevelRol | null {
  return GEVEL_ROLLEN[(naamArbeid ?? '').trim().toUpperCase()] ?? null;
}

/** "met voeg" staat in de omschrijving op de fiche; standaard zonder voeg. */
export function isMetVoeg(omschrijving: string): boolean {
  const s = omschrijving.toLowerCase();
  return /\bmet\s+voeg/.test(s) && !/\bzonder\s+voeg/.test(s);
}

/** Bevat de calculatie posten die door de gevelladder geprijsd worden? */
export function isGevelCalculatie(matchedLines: MatchedLine[], meerwerkLines: MeerwerkLine[] = []): boolean {
  const rol = (n: string) => gevelRol(n);
  return (
    matchedLines.some((l) => rol(l.naamArbeid) === 'crepi' || rol(l.naamArbeid) === 'steenstrips') ||
    meerwerkLines.some((m) => rol(m.naamArbeid) === 'crepi' || rol(m.naamArbeid) === 'steenstrips')
  );
}

export interface GevelResultaat {
  actief: boolean;
  variant: GevelVariant;
  /** Ladder op de m2 uit de werkposten. */
  werkposten: CrepiResultaat;
  /** Ladder op werkposten + meerwerk: extra m2 crepi schuiven de schijven mee (SPEC_calculatie 11). */
  metMeerwerk: CrepiResultaat;
  crepiMeerwerk: number;
  steenstrips: {
    m2ZonderVoeg: number;
    m2MetVoeg: number;
    m2MeerwerkZonderVoeg: number;
    m2MeerwerkMetVoeg: number;
    bedragWerkposten: number;
    bedragMeerwerk: number;
  };
  /** Werkposten en meerwerkregels waarvan het bedrag door dit blok vervangen wordt. */
  vervangenLijnIds: string[];
  vervangenMeerwerkIds: string[];
  totaalWerkposten: number;
  totaalMeerwerk: number;
  waarschuwingen: string[];
}

export function berekenGevel(
  matchedLines: MatchedLine[],
  meerwerkLines: MeerwerkLine[],
  cfg: GevelConfig,
): GevelResultaat {
  let m2Crepi = 0;
  let m2Iso = 0;
  let m2CrepiMw = 0;
  let m2IsoMw = 0;
  let ssZonder = 0;
  let ssMet = 0;
  let ssMwZonder = 0;
  let ssMwMet = 0;
  const vervangenLijnIds: string[] = [];
  const vervangenMeerwerkIds: string[] = [];

  for (const l of matchedLines) {
    const rol = gevelRol(l.naamArbeid);
    if (!rol) continue;
    vervangenLijnIds.push(l.id);
    if (rol === 'crepi') m2Crepi += l.hoeveelheid || 0;
    else if (rol === 'gevel_isolatie') m2Iso += l.hoeveelheid || 0;
    else {
      // Steenstrips: per PDF-regel kijken of het met of zonder voeg is.
      if (l.subItems.length) {
        for (const s of l.subItems) {
          if (isMetVoeg(s.pdfNaam)) ssMet += s.hoeveelheid || 0;
          else ssZonder += s.hoeveelheid || 0;
        }
      } else {
        ssZonder += l.hoeveelheid || 0;
      }
    }
  }
  for (const m of meerwerkLines) {
    const rol = gevelRol(m.naamArbeid);
    if (!rol) continue;
    vervangenMeerwerkIds.push(m.id);
    if (rol === 'crepi') m2CrepiMw += m.hoeveelheid || 0;
    else if (rol === 'gevel_isolatie') m2IsoMw += m.hoeveelheid || 0;
    else if (isMetVoeg(m.pdfNaam)) ssMwMet += m.hoeveelheid || 0;
    else ssMwZonder += m.hoeveelheid || 0;
  }

  const werkposten = berekenCrepi(m2Crepi, m2Iso, cfg.ladder);
  const metMeerwerk = berekenCrepi(m2Crepi + m2CrepiMw, m2Iso + m2IsoMw, cfg.ladder);
  const crepiMeerwerk = round2(metMeerwerk.totaal - werkposten.totaal);
  const ssWerk = round2(ssZonder * cfg.steenstrips.zonderVoeg + ssMet * cfg.steenstrips.metVoeg);
  const ssMw = round2(ssMwZonder * cfg.steenstrips.zonderVoeg + ssMwMet * cfg.steenstrips.metVoeg);

  const actief = isGevelCalculatie(matchedLines, meerwerkLines);
  return {
    actief,
    variant: cfg.variant,
    werkposten,
    metMeerwerk,
    crepiMeerwerk,
    steenstrips: {
      m2ZonderVoeg: ssZonder,
      m2MetVoeg: ssMet,
      m2MeerwerkZonderVoeg: ssMwZonder,
      m2MeerwerkMetVoeg: ssMwMet,
      bedragWerkposten: ssWerk,
      bedragMeerwerk: ssMw,
    },
    vervangenLijnIds: actief ? vervangenLijnIds : [],
    vervangenMeerwerkIds: actief ? vervangenMeerwerkIds : [],
    totaalWerkposten: actief ? round2(werkposten.totaal + ssWerk) : 0,
    totaalMeerwerk: actief ? round2(crepiMeerwerk + ssMw) : 0,
    // Bij steenstrips is de EPS inbegrepen ("inclusief eps en cementeren"): een overschot aan
    // isolatie t.o.v. de crepi wordt dan niet betaald, maar dat moet de controleur wel zien.
    waarschuwingen: ssZonder + ssMet + ssMwZonder + ssMwMet > 0 && metMeerwerk.m2Isolatie > metMeerwerk.m2Crepi
      ? [...metMeerwerk.waarschuwingen, 'Isolatie onder steenstrips zit in de steenstripsprijs (inclusief eps) en wordt niet apart betaald.']
      : metMeerwerk.waarschuwingen,
  };
}

// === Accessoires en extra werken aan de tarieven van de gevellijst (SPEC_calculatie 7.1) ===
//
// Beslissing Jordy 27 okt 2026: nieuwe gevelcalculaties betalen de accessoires aan de tarieven van
// gevellijst A/B, niet aan de algemene matchinglijst van de ploeg (waar o.a. raamdorpels, afvoerbuizen
// en granietplint op NVT staan). Conservatief: enkel herkende omschrijvingen én een eenheid die
// overeenkomt; schouwen vallen hier niet onder (aaneensluitend of los vraagt een beslissing, 9).

/** Omschrijving op de fiche → postcode in de gevellijst. Volgorde telt: specifiek vóór algemeen. */
export const GEVEL_ACCESSOIRE_HERKENNING: Array<{ code: string; patroon: RegExp }> = [
  { code: 'gevelplint_granietpleister', patroon: /GRANIET(KORREL|PLEISTER)/ },
  { code: 'gevelplint_blauwsteen', patroon: /GEVELPLINT.*BLAUWE?\s*STEEN/ },
  { code: 'raamdorpels_alu', patroon: /RAAMDORPEL.*ALU/ },
  { code: 'raamdorpels_blauwe_steen', patroon: /RAAMDORPEL.*BLAUWE?\s*STEEN/ },
  { code: 'deurdorpels', patroon: /DEURDORPEL/ },
  { code: 'afvoerbuizen', patroon: /A[F]?VOERBUI(S|ZEN)/ },
  { code: 'zinkprofielen', patroon: /DAKRANDPROFIEL.*ZINK|ZINKPROFIEL|ZINKEN DAKRAND/ },
  { code: 'hanggoot', patroon: /HANGGOT|HANGGOOT/ },
  { code: 'afwerking_bakgoot_volkern', patroon: /(BAKGOOT|OVERSTEEK).*VOLKERN/ },
  { code: 'verwijderen_asbest', patroon: /ASBEST.*GEVEL/ },
  { code: 'verwijderen_gevelbekleding', patroon: /VERWIJDEREN.*GEVELBEKLEDING/ },
  { code: 'afslijpen_dagkanten_kopschutten', patroon: /AFSLIJPEN.*DAGKANT.*KOPSCHUT/ },
  { code: 'afslijpen_dagkanten', patroon: /AFSLIJPEN.*DAGKANT/ },
  { code: 'afbraak_gemetste_muur', patroon: /GEMETSTE MUUR|BUITENMUUR VERWIJDEREN/ },
  { code: 'afbraak_blauwe_steen', patroon: /AFBRAAK.*BLAUWE?\s*STEEN/ },
  { code: 'verlengen_kraan', patroon: /KRAAN/ },
  { code: 'verlengen_verlichting', patroon: /VERLICHTING/ },
  { code: 'luifel', patroon: /LUIFEL/ },
  { code: 'bevestigingsblokken_kepers', patroon: /BEVESTIGINGSBLOK/ },
  { code: 'km_vergoeding', patroon: /KM[- ]?VERGOEDING/ },
];

export function herkenGevelAccessoire(omschrijving: string): string | null {
  const s = (omschrijving ?? '').toUpperCase();
  return GEVEL_ACCESSOIRE_HERKENNING.find((h) => h.patroon.test(s))?.code ?? null;
}

function eenhedenPassen(fiche: string, post: string): boolean {
  const f = (fiche ?? '').trim().toUpperCase().replace('²', '2');
  const p = (post ?? '').trim().toUpperCase();
  return !f || f === p;
}

/**
 * Prijst de herkende accessoires van een gevelcalculatie aan de tarieven van de gevellijst:
 *  - een gematchte werkpost met een herkende omschrijving en passende eenheid krijgt de prijs van 7.1;
 *  - een regel die enkel overgeslagen werd omdat de algemene lijst hem op NVT zette, wordt een werkpost.
 * Posten die de ladder prijst (crepi, isolatie, steenstrips) en posten met een afwijkende prijs
 * (reden ingevuld) blijven onaangeroerd. Zonder accessoires in de config gebeurt er niets.
 */
export function pasGevelAccessoiresToe(
  input: { matchedLines: MatchedLine[]; skippedItems?: SkippedItem[]; parsedItems?: ParsedItem[] },
  cfg: GevelConfig | null,
  opts: { nvtReden?: (reden: string) => boolean; plItemIdVoorCode?: (code: string) => string | null } = {},
): { matchedLines: MatchedLine[]; skippedItems: SkippedItem[]; aangepast: number } {
  const acc = cfg?.accessoires;
  const skipped = input.skippedItems ?? [];
  if (!acc) return { matchedLines: input.matchedLines, skippedItems: skipped, aangepast: 0 };
  let aangepast = 0;

  const matchedLines = input.matchedLines.map((l) => {
    // Eén keer geprijsd blijft geprijsd: een latere prijsaanpassing door de gebruiker blijft staan.
    if (gevelRol(l.naamArbeid) || l.schijven || l.gevelAccessoire || l.prijsAfwijkingReden?.trim()) return l;
    const omschrijvingen = l.subItems.length ? l.subItems.map((s) => s.pdfNaam) : [l.naamArbeid];
    const codes = [...new Set(omschrijvingen.map(herkenGevelAccessoire))];
    const code = codes.length === 1 ? codes[0] : null;
    const post = code ? acc[code] : undefined;
    if (!code || !post || !eenhedenPassen(l.eenheid, post.eenheid)) return l;
    aangepast++;
    const subItems = l.subItems.map((s) => ({ ...s, prijs: post.prijs, subtotaal: round2((s.hoeveelheid || 0) * post.prijs) }));
    return {
      ...l,
      gevelAccessoire: code,
      prijs: post.prijs,
      subItems,
      subtotaal: round2((l.hoeveelheid || 0) * post.prijs),
    };
  });

  // Regels die de algemene lijst op NVT zette maar die in 7.1 wel betaald worden.
  const isNvt = opts.nvtReden ?? ((r: string) => /NVT|niet van toepassing/i.test(r));
  const parsedById = new Map((input.parsedItems ?? []).map((p) => [p.id, p] as const));
  const rest: SkippedItem[] = [];
  for (const sk of skipped) {
    const p = parsedById.get(sk.parsedItemId);
    const code = herkenGevelAccessoire(sk.pdfNaam);
    const post = code ? acc[code] : undefined;
    if (!p || !code || !post || !isNvt(sk.reden) || !eenhedenPassen(p.eenheid, post.eenheid)) {
      rest.push(sk);
      continue;
    }
    const hv = p.werkelijkeHoeveelheid || 0;
    const plItemId = opts.plItemIdVoorCode?.(code) ?? `gevel:${code}`;
    aangepast++;
    matchedLines.push({
      id: `ml_acc_${p.id}`,
      naamArbeid: post.naam,
      eenheid: post.eenheid,
      prijs: post.prijs,
      hoeveelheid: hv,
      subtotaal: round2(hv * post.prijs),
      consumedParsedItemIds: [p.id],
      autoMatched: true,
      gevelAccessoire: code,
      subItems: [{
        parsedItemId: p.id, pdfNaam: sk.pdfNaam, plItemId, hoeveelheid: hv, eenheid: post.eenheid,
        prijs: post.prijs, subtotaal: round2(hv * post.prijs), confidence: 1, autoMatched: true, matchSource: 'exact',
      }],
    });
  }
  return { matchedLines, skippedItems: rest, aangepast };
}
